/**
 * Add Admin User Page
 * Create a new administrator account
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { AdminUserService } from '../../services/adminUser/AdminUserService';
import { canManageAdminUsers } from '../../utils/permissions';
import type { AdminUserRole } from '../../types/adminUser';
import { adminUserRoleLabels } from '../../types/adminUser';
import { Card, CardContent, TextField, Select, PrimaryButton, SecondaryButton } from '../../components/ui';

const calculatePasswordStrength = (password: string): { strength: 'weak' | 'medium' | 'strong'; value: number } => {
  if (!password) {
    return { strength: 'weak', value: 0 };
  }

  let strength = 0;

  // Length check
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 10;

  // Character variety checks
  if (/[a-z]/.test(password)) strength += 15;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 15;

  // Bonus for longer passwords
  if (password.length >= 16) strength += 10;

  if (strength < 40) {
    return { strength: 'weak', value: strength };
  } else if (strength < 70) {
    return { strength: 'medium', value: strength };
  } else {
    return { strength: 'strong', value: strength };
  }
};

const getPasswordStrengthColor = (strength: 'weak' | 'medium' | 'strong', theme: Theme): string => {
  switch (strength) {
    case 'weak':
      return theme.palette.error.main;
    case 'medium':
      return theme.palette.warning.main;
    case 'strong':
      return theme.palette.success.main;
    default:
      return theme.palette.text.secondary;
  }
};

const AddAdminUserPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<AdminUserRole>('support');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    role?: string;
  }>({});

  const passwordStrength = calculatePasswordStrength(password);

  // Check permission
  useEffect(() => {
    if (!canManageAdminUsers()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Check if all required fields are filled
  const isFormValid = (): boolean => {
    const trimmedEmail = email.trim();

    return !!(
      firstName.trim() &&
      lastName.trim() &&
      trimmedEmail &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail) &&
      password &&
      password.length >= 8 &&
      confirmPassword &&
      role
    );
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    const trimmedEmail = email.trim();

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!trimmedEmail) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await AdminUserService.createAdminUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        role,
      });
      // Navigate back to admin users list
      navigate('/settings/admin-users');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create admin user';
      setErrors({ email: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings/admin-users');
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          marginBottom: theme.spacing(3),
        }}
      >
        Add New Administrator
      </Typography>

      {/* Form Card */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(3),
              fontSize: theme.typography.body1.fontSize,
            }}
          >
            Administrator Details
          </Typography>

          <Grid container spacing={3}>
            {/* First Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                required
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errors.firstName) {
                    setErrors({ ...errors, firstName: undefined });
                  }
                }}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                required
                placeholder="Enter last name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errors.lastName) {
                    setErrors({ ...errors, lastName: undefined });
                  }
                }}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                required
                placeholder="Enter email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors({ ...errors, email: undefined });
                  }
                }}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Role */}
            <Grid item xs={12} sm={6}>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value as AdminUserRole)}
                label="Role/Permissions"
                formControlProps={{
                  fullWidth: true,
                  required: true,
                }}
              >
                <MenuItem value="super_admin">{adminUserRoleLabels.super_admin}</MenuItem>
                <MenuItem value="support">{adminUserRoleLabels.support}</MenuItem>
              </Select>
            </Grid>

            {/* Password */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                required
                placeholder="Enter password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) {
                    setErrors({ ...errors, password: undefined });
                  }
                }}
                error={!!errors.password}
                helperText={errors.password || 'Must be at least 8 characters long.'}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Confirm Password */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Password"
                required
                placeholder="Re-enter password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) {
                    setErrors({ ...errors, confirmPassword: undefined });
                  }
                }}
                error={!!errors.confirmPassword || (!!confirmPassword && password !== confirmPassword)}
                helperText={errors.confirmPassword || (confirmPassword && password !== confirmPassword ? 'Passwords do not match' : '')}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Password Strength */}
            {password && (
              <Grid item xs={12}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: theme.typography.caption.fontSize,
                      }}
                    >
                      Password Strength
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: getPasswordStrengthColor(passwordStrength.strength, theme),
                        fontSize: theme.typography.caption.fontSize,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                      }}
                    >
                      {passwordStrength.strength}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength.value}
                    sx={{
                      height: 6,
                      borderRadius: theme.shapes.borderRadius.md,
                      backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getPasswordStrengthColor(passwordStrength.strength, theme),
                      },
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.spacing(2),
              marginTop: theme.spacing(4),
            }}
          >
            <SecondaryButton onClick={handleCancel}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting ? 'Adding...' : 'Add Administrator'}
            </PrimaryButton>
          </Box>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default AddAdminUserPage;
